import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAccountContext } from "../../strict/app/AccountResolver";
import { toCreateUrl } from "../Util";

// Inspired from - https://dev.to/gabe_ragland/debouncing-with-react-hooks-jci
const useDebounce = <T,>(value: T, initialDelay: number) => {
  // State and setters for debounced value
  const [delay] = useState(initialDelay);
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Set debouncedValue to value (passed in) after the specified delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Return a cleanup function that will be called every time ...
      // ... useEffect is re-called. useEffect will only be re-called ...
      // ... if value changes (see the inputs array below).
      // This is how we prevent debouncedValue from changing if value is ...
      // ... changed within the delay period. Timeout gets cleared and restarted.
      // To put it in context, if the user is typing within our app's ...
      // ... search box, we don't want the debouncedValue to update until ...
      // ... they've stopped typing for more than 500ms.
      return () => {
        clearTimeout(handler);
      };
    },
    // Only re-call effect if value changes
    // You could also add the "delay" var to inputs array if you ...
    // ... need to be able to change that dynamically.
    [value, delay]
  );

  return debouncedValue;
};

interface declarationProps {
  changelist_id?: string;
  project?: string;
  line?: number;
}

interface optionsProps {
  addReferrer: boolean;
  addSearchReferrer?: boolean;
}

const useNavigator = () => {
  const { urlName } = useAccountContext();
  const navigate = useNavigate();
  const location = useLocation();

  const route = (
    url_part?: Array<string> | string,
    params?: Record<string, string | number | undefined>,
    options: optionsProps = {
      addReferrer: false,
      addSearchReferrer: false,
    }
  ) => {
    const { addReferrer = false, addSearchReferrer = false } = options;
    if (addReferrer && params) {
      if (addSearchReferrer) {
        params.referrer = location.pathname + location.search;
      } else {
        params.referrer = location.pathname;
      }
    }
    return `/app/${urlName}/${toCreateUrl(url_part, params)}`;
  };

  const goto = (
    url_part?: Array<string> | string,
    params?: Record<string, string | number | undefined>,
    options?: optionsProps
  ) => {
    navigate(route(url_part, params, options));
  };

  return {
    goto: goto,

    gotoDeclaration: (declaration: declarationProps) => {
      goto(["scripts", "editor", declaration.changelist_id || "", declaration.project || ""], {
        line: declaration.line,
      });
    },

    route: route,
  };
};

const useDebounceSearchParams = (paramName: string, paramValue: string, initialDelay: number) => {
  // This function is based on useDebounce
  const [delay] = useState(initialDelay);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchParams((params) => {
        params.set(paramName, paramValue);
        return params;
      });
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [delay, setSearchParams, paramValue, paramName]);

  return searchParams;
};

type UseNavigatorType = ReturnType<typeof useNavigator>;

export { useDebounce, useDebounceSearchParams, useNavigator, UseNavigatorType };
